import type { CSSProperties } from 'react';
import type { SessionState } from '../lib/session.js';
import { useState } from 'react';
import { modulesOf, personaDeLlamada, type Snapshot } from '../lib/snapshot-types.js';
import { hace } from '../lib/fechas.js';
import { alcanceCompat, normalizarSeleccion, type SeleccionPersonas } from '../lib/filtro-panel.js';
import { DateRangePicker, type DateRangeValue } from './DateRangePicker.js';
import { MetricsGrid } from './MetricsGrid.js';
import { navigate } from '../lib/hash-route.js';
import { QuarterlyGoals, goalsForView } from './QuarterlyGoals.js';
import { etiquetaTrimestre, eurosCompacto } from '../lib/etiquetas-metricas.js';
import { PersonasSelect } from './PersonasSelect.js';
import { ResumenPeriodo } from './ResumenPeriodo.js';
import { ReunionesSeccion } from './ReunionesSeccion.js';
import { EmbudoVentas } from './EmbudoVentas.js';
import { SecuenciaAnillos } from './SecuenciaAnillos.js';

interface Props {
  session?: SessionState;
  snapshot: Snapshot;
  user: { id: string; email: string; name?: string; role: string };
  /** Las personas elegidas en el desplegable (por defecto, todas). */
  personas?: SeleccionPersonas;
  onPersonasChange?: (sel: SeleccionPersonas) => void;
  range: DateRangeValue;
  onRangeChange: (r: DateRangeValue) => void;
  streamStatus?: 'connecting' | 'open' | 'closed';
}

/**
 * EL PANEL (revamp del 6-oct): una cabecera con el periodo real y las personas,
 * la fila resumen del periodo, las reuniones y el embudo lado a lado, la secuencia
 * (16-oct, donde esta encendida), el reparto por persona y las llamadas relevantes.
 * Todo sale del MISMO periodo y de las MISMAS personas (el embudo y la secuencia son
 * lo de ahora, y lo dicen). Lo que el inquilino no tiene (por modulo) no se pinta.
 */
export function SnapshotView({ snapshot, session, user, personas = 'todas', onPersonasChange, range, onRangeChange, streamStatus }: Props) {
  const modules = modulesOf(snapshot);
  const sel = normalizarSeleccion(personas, snapshot.personas);
  // Para lo que aun habla en "me"/"team" (objetivos de un snapshot de antes, ganadas de Zoho).
  const alcance = alcanceCompat(sel, snapshot.personas);
  // Un objetivo de reuniones cuyo conseguido no se sabe no se compara contra nada.
  const objetivoSinMedir = goalsForView(snapshot, alcance, user.email)
    .some((g) => g.metric === 'meetings' && (g.achieved === null || (!snapshot.filtro && alcance === 'me' && g.user_email === null)));
  return (
    <div className="panel">
      <Topbar snapshot={snapshot} user={user} sel={sel} onPersonasChange={onPersonasChange} range={range} onRangeChange={onRangeChange} streamStatus={streamStatus} />
      <ResumenPeriodo snapshot={snapshot} objetivoSinMedir={objetivoSinMedir} />
      <QuarterlyGoals snapshot={snapshot} scope={alcance} email={user.email} />
      <div className="panel-dos">
        <ReunionesSeccion snapshot={snapshot} session={session} />
        <EmbudoVentas snapshot={snapshot} scope={alcance} />
      </div>
      {/* La secuencia, solo donde el servidor la manda (16-oct). */}
      <SecuenciaAnillos snapshot={snapshot} />
      {/* El desglose viejo de objetivos solo llega en snapshots de antes. */}
      {snapshot.quarterly_goals === undefined && modules.meeting_targets && <MeetingsTargetsSection m={snapshot.meetings} reps={snapshot.reps} scope={alcance} />}
      <h2 className="panel-subtitulo">Por persona</h2>
      <MetricsGrid snapshot={snapshot} scope={alcance} />
      {modules.calls && <RelevantCallsSection snapshot={snapshot} />}
      {/* Por modulo: el inquilino que lo apaga (fluxo) ya no lo quiere en el panel. */}
      {modules.acciones_requeridas && <RequiredActionsSection items={snapshot.required_actions} />}
    </div>
  );
}

function Topbar({ snapshot, user, sel, onPersonasChange, range, onRangeChange, streamStatus }: {
  snapshot: Snapshot;
  user: { id: string; email: string; name?: string; role: string };
  sel: SeleccionPersonas;
  onPersonasChange?: (sel: SeleccionPersonas) => void;
  range: DateRangeValue;
  onRangeChange: (r: DateRangeValue) => void;
  streamStatus?: 'connecting' | 'open' | 'closed';
}) {
  const statusColor = streamStatus === 'open' ? 'var(--ok)' : streamStatus === 'connecting' ? 'var(--warn)' : 'var(--fg-subtle)';
  const statusLabel = streamStatus === 'open' ? 'tiempo real' : streamStatus === 'connecting' ? 'conectando' : 'desconectado';
  const personas = snapshot.personas ?? [];
  // El saludo, con el nombre de la persona (nunca la parte local de un email).
  const nombre = personas.find((p) => p.yo)?.nombre ?? user.name?.trim() ?? null;
  const conObjetivos = (snapshot.quarterly_goals?.length ?? 0) > 0;
  return (
    <header className="topbar">
      <div className="topbar-izq">
        <div className="topbar-brand">
          {snapshot.client.name}
          {streamStatus && (
            <span className="status-dot">
              <span className="dot" style={{ background: statusColor, boxShadow: streamStatus === 'open' ? `0 0 6px ${statusColor}` : 'none' }} />
              {statusLabel}
            </span>
          )}
        </div>
        <h1 className="topbar-title">{nombre ? `Hola ${nombre}` : 'Panel de ventas'}</h1>
        {/* El periodo y las personas ya se leen en sus dos botones (revision del 7-oct: cada cosa una
            vez). El trimestre y sus dias, solo con objetivos (o en un snapshot de antes). */}
        {(!snapshot.filtro || conObjetivos) && (
          <div className="topbar-meta">
            <strong>{etiquetaTrimestre(snapshot.quarter.label)}</strong>
            <span>·</span>
            <span>{snapshot.quarter.daysLeft} días restantes</span>
            <span>·</span>
            <span>{Math.round(snapshot.quarter.progressPct * 100)}% transcurrido</span>
          </div>
        )}
      </div>
      <div className="topbar-actions">
        {personas.length > 0 && onPersonasChange && (
          <PersonasSelect personas={personas} seleccion={sel} onChange={onPersonasChange} />
        )}
        <DateRangePicker value={range} onChange={onRangeChange} periodo={snapshot.filtro ? { desde: snapshot.filtro.desde, hasta: snapshot.filtro.hasta } : undefined} />
      </div>
    </header>
  );
}

function RequiredActionsSection({ items }: { items: Snapshot['required_actions'] }) {
  const [expanded, setExpanded] = useState(false);
  const TOP_N = 3;
  const visible = expanded ? items : items.slice(0, TOP_N);
  const hidden = Math.max(0, items.length - TOP_N);
  return (
    <section className="section">
      <div className="section-title">Acciones requeridas · oportunidades estancadas {items.length > 0 ? `· ${items.length}` : ''}</div>
      {items.length === 0 ? (
        <p className="section-empty">Sin oportunidades estancadas. ¡Bien!</p>
      ) : (
        <>
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {visible.map((a) => (
              <li key={a.link ?? a.lead} className="row-item" style={{ cursor: 'default' }}>
                <div style={{ flex: 1 }}>
                  <div className="row-title">{a.lead}</div>
                  <div className="row-sub">{a.reason}</div>
                  <div className="row-sub" style={{ color: 'var(--ok)' }}>→ {a.suggested_next}</div>
                </div>
                <div className="row-aside" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
                  <div>{a.rep ?? '-'}</div>
                  {a.value_eur ? <div>{eurosCompacto(a.value_eur)}</div> : null}
                  {a.link && (
                    <button
                      className="action-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        // a.link is an absolute https URL to GHL when the backend
                        // can build it (has ghl_location_id + contact_id).
                        const url = /^https?:\/\//.test(a.link!) ? a.link! : null;
                        if (url) window.open(url, '_blank', 'noopener');
                      }}
                      title="Abrir conversación del contacto en GHL (email / WhatsApp / SMS / llamada en una sola vista)"
                    >
                      Abrir en GHL ↗
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
          {hidden > 0 && (
            <button
              className="tab"
              style={{ marginTop: 10 }}
              onClick={() => setExpanded(!expanded)}
            >
              {expanded ? '↑ Ver menos' : `↓ Ver ${hidden} más`}
            </button>
          )}
        </>
      )}
    </section>
  );
}

function RelevantCallsSection({ snapshot }: { snapshot: Snapshot }) {
  const items = snapshot.relevant_calls;
  if (items.length === 0) return null;
  return (
    <section className="section">
      <div className="section-title">Llamadas relevantes · haz clic para escuchar y ver la rúbrica</div>
      <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 10 }}>
        {items.map((c) => {
          const score = typeof c.score === 'number' ? c.score : c.score !== null ? Number(c.score) : null;
          const scoreColor = score === null ? 'var(--fg-subtle)'
            : score >= 70 ? 'var(--ok)'
            : score >= 40 ? 'var(--warn)'
            : 'var(--danger)';
          return (
            <li
              key={c.call_id}
              onClick={() => navigate({ kind: 'call', id: c.call_id })}
              style={{
                display: 'flex', gap: 12, padding: 12, borderRadius: 8,
                border: '1px solid var(--border)', background: 'var(--surface)',
                cursor: 'pointer', transition: 'border-color 120ms, box-shadow 120ms',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--accent)'; e.currentTarget.style.boxShadow = '0 1px 3px rgba(37,99,235,.1)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.boxShadow = 'none'; }}
            >
              {/* Score circle */}
              <div style={{
                flexShrink: 0, width: 48, height: 48, borderRadius: '50%',
                background: 'var(--surface-hi)', border: `2px solid ${scoreColor}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 16, fontWeight: 700, color: scoreColor,
              }}>
                {score === null ? '-' : Math.round(score)}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--fg)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {c.contact_name ?? '(sin nombre)'}
                </div>
                {c.company && (
                  <div style={{ fontSize: 11, color: 'var(--fg-subtle)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c.company}</div>
                )}
                <div style={{ fontSize: 11, color: 'var(--fg-muted)', marginTop: 4, display: 'flex', gap: 8 }}>
                  <span>▶ {c.duration_sec} s</span>
                  <span>·</span>
                  <span title={c.started_at}>{hace(c.started_at)}</span>
                  <span>·</span><span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{personaDeLlamada(snapshot, c)}</span>
                </div>
                {c.suggested_followup && (
                  <div style={{ fontSize: 11, color: 'var(--ok)', marginTop: 6, overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical' }}>
                    → {c.suggested_followup}
                  </div>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function MeetingsTargetsSection({ m, reps, scope }: { m: Snapshot['meetings']; reps: Snapshot['reps']; scope: 'me' | 'team' }) {
  if (m.target_breakdown.length === 0) {
    // Un objetivo de EMPRESA no tiene desglose por comercial, y decir "sin
    // targets definidos" con un objetivo declarado delante era contradecir al
    // propio snapshot.
    return (
      <section className="section">
        <div className="section-title">Reuniones · objetivo</div>
        {m.target === null
          ? <div className="section-empty">Sin objetivo declarado{scope === 'me' ? ' para ti' : ''}.</div>
          : <div className="section-empty">Objetivo de empresa: {m.target} reuniones. Celebradas: {m.progress_count}. Sin objetivos por comercial declarados.</div>}
      </section>
    );
  }
  return (
    <section className="section">
      <div className="section-title">Reuniones · objetivo {m.target} · por persona</div>
      <table className="table">
        <thead>
          <tr><th>Persona</th><th>Objetivo</th><th>Conseguidas</th><th>%</th></tr>
        </thead>
        <tbody>
          {m.target_breakdown.map((b) => (
            <tr key={b.user_email}>
              <td>{reps[b.user_email]?.name?.trim() || b.user_email}</td>
              <td>{b.target}</td>
              <td>{b.achieved}</td>
              <td>
                <span className={`badge ${b.pct >= 1 ? 'badge-ok' : b.pct >= 0.6 ? 'badge-warn' : 'badge-danger'}`}>
                  {Math.round(b.pct * 100)}%
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}

// inline style helper kept for any leftover usage
export const cardSection: CSSProperties = {};
export const cardH2: CSSProperties = {};
